/* eslint-disable no-console */
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AccountAPI from '../../api/account';
import OnboardingAPI from '../../api/onboarding';
import { differenceInDays } from 'date-fns';
import EnterpriseAccountAPI from '../../api/enterprise/account';
import { throwErrorMessage } from '../utils/api';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';

const findRecordById = ($state, id) =>
  $state.records.find(record => record.id === Number(id)) || {};

const TRIAL_PERIOD_DAYS = 15;

// Polar/Paymable expose the payment interval on a subscription in several
// shapes depending on the source. Auto-activated trials use
// `metadata.product.interval`, older Payvable responses use
// `metadata.recurring_interval` or `metadata.repeat_payment_interval`, and
// newer Polar responses nest it under `metadata.pricing.*`. One-time credit
// pack purchases have no interval anywhere. Treat any of these as a real
// recurring platform subscription so trials aren't misclassified as credit
// packs and hidden from the billing page.
const isRecurringSubscription = sub =>
  !!(
    sub?.metadata?.recurring_interval ||
    sub?.metadata?.pricing?.recurring_interval ||
    sub?.metadata?.repeat_payment_interval ||
    sub?.metadata?.pricing?.repeat_payment_interval ||
    sub?.metadata?.product?.interval ||
    sub?.metadata?.auto_activated === true
  );

const state = {
  records: [],
  plan: {},
  invoices: [],
  affiliateProgram: {},
  creditBalance: {
    balance: null,
    consumed: 0,
    totalCredited: 0,
    exhausted: false,
    source: null,
  },
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isUpdating: false,
    isCheckoutInProcess: false,
    isFetchingLimits: false,
    isFetchingCreditBalance: false,
  },
};

export const getters = {
  getAccount: $state => id => {
    return findRecordById($state, id);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },

  getPlan($state) {
    return $state.plan;
  },

  getInvoices($state) {
    return $state.invoices;
  },

  getAffiliateProgram($state) {
    return $state.affiliateProgram;
  },

  getCreditBalance($state) {
    return $state.creditBalance;
  },

  isFetchingCreditBalance: $state =>
    $state.uiFlags.isFetchingCreditBalance === true,

  isRTL: ($state, _getters, rootState, rootGetters) => {
    const accountId = Number(rootState.route?.params?.accountId);
    const userLocale = rootGetters?.getUISettings?.locale;
    const accountLocale =
      accountId && findRecordById($state, accountId)?.locale;

    // Prefer user locale; fallback to account locale
    const effectiveLocale = userLocale ?? accountLocale;

    return effectiveLocale ? getLanguageDirection(effectiveLocale) : false;
  },
  isTrialAccount: $state => id => {
    const account = findRecordById($state, id);
    const createdAt = new Date(account.created_at);
    const diffDays = differenceInDays(new Date(), createdAt);

    return diffDays <= TRIAL_PERIOD_DAYS;
  },
  isFeatureEnabledonAccount: $state => (id, featureName) => {
    const account = findRecordById($state, id);
    const features = account?.features || {};
    return features[featureName] || false;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await AccountAPI.get();
      commit(types.default.ADD_ACCOUNT, response.data);
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },
  update: async ({ commit }, { options, ...updateObj }) => {
    if (options?.silent !== true) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    }

    try {
      const response = await AccountAPI.update('', updateObj);
      commit(types.default.EDIT_ACCOUNT, response.data);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  finishOnboarding: async ({ commit }, payload) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      const response = await OnboardingAPI.update(payload);
      commit(types.default.EDIT_ACCOUNT, response.data);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, { id }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      await AccountAPI.delete(id);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  toggleDeletion: async (
    { commit },
    { action_type } = { action_type: 'delete' }
  ) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      await EnterpriseAccountAPI.toggleDeletion(action_type);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  create: async ({ commit }, accountInfo) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: true });
    try {
      const response = await AccountAPI.createAccount(accountInfo);
      const account_id = response.data.data.account_id;
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      return account_id;
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      throw error;
    }
  },

  openBillingPortal: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.openBillingPortal();
      // V2 returns checkout_url, redirect_url for backward compatibility
      // customer_portal returns portal_url
      const url =
        response.data?.checkout_url ||
        response.data?.redirect_url ||
        response.data?.portal_url;
      if (url) {
        window.location = url;
      } else {
        const errorMsg =
          response.data?.error ||
          'No redirect URL received from billing service';
        throw new Error(errorMsg);
      }
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  // Legacy alias retained for older callers.
  checkout: async ({ dispatch }) => {
    return dispatch('openBillingPortal');
  },

  // Regenerate checkout session for incomplete trial subscription
  // This creates a fresh checkout URL every time the user clicks "Start Free Trial"
  regenerateCheckout: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.regenerateCheckout();
      // Redirect to the fresh checkout URL
      window.open(response.data.checkout_url, '_blank');
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  fetchCurrentSubscription: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.getSubscriptions();
      let subscriptionData = response.data;

      if (response.data.items && response.data.items.length > 0) {
        // Credit pack purchases come back as subscriptions in Polar (they're
        // one-time products). Filter them out so they don't pollute the
        // Current Plan display - they belong in the separate Sage Credits
        // widget.
        const platformSubs = response.data.items.filter(
          isRecurringSubscription
        );

        if (platformSubs.length > 0) {
          // Prefer an actually-active platform subscription; fall back to most recent
          const activeItem = platformSubs.find(
            item => item.status === 'active' || item.status === 'trialing'
          );
          subscriptionData = activeItem || platformSubs[0];
        } else {
          // No recurring subscription - all were one-time purchases (credit packs)
          subscriptionData = { items: [] };
        }
      }

      commit(types.default.SET_SUBSCRIPTION, subscriptionData);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  // Legacy alias retained for older callers.
  subscription: async ({ dispatch }) => {
    return dispatch('fetchCurrentSubscription');
  },

  refreshSubscription: async ({ commit }) => {
    try {
      const response = await EnterpriseAccountAPI.refreshSubscription();
      // refresh endpoint redirects to index, which returns { items: [...] }
      const items = response.data?.items || response.data || [];
      // Filter out one-time products (credit packs) - they're not the
      // platform subscription. Real subs have a payment interval set; see
      // isRecurringSubscription for the recognized shapes.
      const platformSubs = items.filter(isRecurringSubscription);
      if (platformSubs.length > 0) {
        const displayItem =
          platformSubs.find(s =>
            ['active', 'trialing', 'past_due', 'canceled'].includes(s.status)
          ) || platformSubs[0];
        commit(types.default.SET_SUBSCRIPTION, displayItem);
      } else if (items.length > 0) {
        // No recurring sub - all were credit packs. Set empty.
        commit(types.default.SET_SUBSCRIPTION, { items: [] });
      }
    } catch (error) {
      console.error('[ACCOUNTS] Error refreshing subscription:', error);
    }
  },

  limits: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingLimits: true });
    try {
      const response = await EnterpriseAccountAPI.getLimits();
      commit(types.default.SET_ACCOUNT_LIMITS, response.data);
    } catch (error) {
      // Limits are non-critical; keep the previous value
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingLimits: false });
    }
  },

  fetchCreditBalance: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, {
      isFetchingCreditBalance: true,
    });
    try {
      const response = await EnterpriseAccountAPI.getCreditBalance();
      const data = response.data || {};
      commit(types.default.SET_CREDIT_BALANCE, {
        balance: typeof data.balance === 'number' ? data.balance : 0,
        consumed: typeof data.consumed === 'number' ? data.consumed : 0,
        totalCredited:
          typeof data.total_credited === 'number' ? data.total_credited : 0,
        exhausted: data.exhausted === true,
        source: data.source || null,
      });
    } catch (error) {
      // Leave prior balance in place on failure so the UI doesn't flash 0.
      console.error('[ACCOUNTS] Error fetching credit balance:', error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingCreditBalance: false,
      });
    }
  },

  invoices: async ({ commit }) => {
    try {
      const response = await EnterpriseAccountAPI.getInvoices();
      // V2 returns items array
      const invoicesData = response.data.items || response.data || [];
      commit(types.default.SET_INVOICES, invoicesData);
    } catch (error) {
      // silent error
      console.error('[ACCOUNTS] Error fetching invoices:', error);
    }
  },

  invoiceUrl: async (_context, orderId) => {
    const response = await EnterpriseAccountAPI.getInvoiceUrl(orderId);
    return response.data?.url;
  },

  // Legacy topup_checkout - uses createCheckout with credits product_id instead
  topup_checkout: async ({ commit }, { product_id }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.createCheckout({
        product_id: product_id,
        quantity: 1,
      });
      window.location =
        response.data.checkout_url || response.data.redirect_url;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  getCacheKeys: async () => {
    return AccountAPI.getCacheKeys();
  },

  // V2 Subscription Actions
  getSubscriptions: async () => {
    try {
      const response = await EnterpriseAccountAPI.getSubscriptions();
      return response.data.items || [];
    } catch (error) {
      console.error('[ACCOUNTS] Error fetching subscriptions:', error);
      return [];
    }
  },

  getSubscription: async (_context, id) => {
    try {
      const response = await EnterpriseAccountAPI.getSubscription(id);
      return response.data;
    } catch (error) {
      console.error('[ACCOUNTS] Error fetching subscription:', error);
      return null;
    }
  },

  createSubscription: async (_context, params) => {
    try {
      const response = await EnterpriseAccountAPI.createSubscription(params);
      return response.data;
    } catch (error) {
      console.error('[ACCOUNTS] Error creating subscription:', error);
      throw error;
    }
  },

  updateSubscription: async (_context, { id, ...params }) => {
    try {
      const response = await EnterpriseAccountAPI.updateSubscription(
        id,
        params
      );
      return response.data;
    } catch (error) {
      console.error('[ACCOUNTS] Error updating subscription:', error);
      throw error;
    }
  },

  updateSeats: async ({ dispatch }, { id, seats }) => {
    try {
      const response = await EnterpriseAccountAPI.updateSubscriptionSeats(
        id,
        seats
      );
      // Polar prorates and bills immediately; pull fresh state + limits so the
      // seat count and agent limit reflect the change without a reload.
      await dispatch('refreshSubscription');
      return response.data;
    } catch (error) {
      console.error('[ACCOUNTS] Error updating subscription seats:', error);
      throw error;
    }
  },

  deleteSubscription: async (_context, { id, reason }) => {
    try {
      await EnterpriseAccountAPI.deleteSubscription(id, reason);
      return { success: true };
    } catch (error) {
      console.error('[ACCOUNTS] Error deleting subscription:', error);
      throw error;
    }
  },

  createCustomer: async () => {
    try {
      const response = await EnterpriseAccountAPI.createCustomer();
      return response.data;
    } catch (error) {
      console.error('[ACCOUNTS] Error creating customer:', error);
      throw error;
    }
  },

  fetchBillingPortalUrl: async () => {
    try {
      const response = await EnterpriseAccountAPI.getCustomerPortal();
      return response.data.portal_url;
    } catch (error) {
      console.error('[ACCOUNTS] Error fetching customer portal:', error);
      return null;
    }
  },

  getCustomerPortal: async ({ dispatch }) => {
    return dispatch('fetchBillingPortalUrl');
  },

  fetchAffiliateProgram: async ({ commit }) => {
    try {
      const response = await EnterpriseAccountAPI.getAffiliateProgram();
      commit(
        types.default.SET_AFFILIATE_PROGRAM,
        response.data.affiliate || {}
      );
      return response.data.affiliate || {};
    } catch (error) {
      console.error('[ACCOUNTS] Error fetching affiliate program:', error);
      return {};
    }
  },

  fetchAffiliateDashboard: async ({ commit }) => {
    try {
      const response = await EnterpriseAccountAPI.getAffiliateDashboard();
      const affiliate = response.data.affiliate || {};
      commit(types.default.SET_AFFILIATE_PROGRAM, affiliate);
      return affiliate;
    } catch (error) {
      console.error('[ACCOUNTS] Error fetching affiliate dashboard:', error);
      return {};
    }
  },

  joinAffiliateProgram: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      const response = await EnterpriseAccountAPI.joinAffiliateProgram();
      commit(
        types.default.SET_AFFILIATE_PROGRAM,
        response.data.affiliate || {}
      );
      return response.data.affiliate || {};
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.default.SET_ACCOUNT_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.ADD_ACCOUNT]: MutationHelpers.setSingleRecord,
  [types.default.EDIT_ACCOUNT]: MutationHelpers.update,
  [types.default.SET_ACCOUNT_LIMITS]: MutationHelpers.updateAttributes,

  [types.default.SET_SUBSCRIPTION]($state, data) {
    $state.plan = {
      ...data,
    };
  },

  [types.default.SET_INVOICES]($state, data) {
    $state.invoices = data;
  },

  [types.default.SET_CREDIT_BALANCE]($state, data) {
    $state.creditBalance = {
      balance: data.balance,
      consumed: data.consumed,
      totalCredited: data.totalCredited,
      exhausted: data.exhausted,
      source: data.source,
    };
  },

  [types.default.SET_AFFILIATE_PROGRAM]($state, data) {
    $state.affiliateProgram = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
